import { ChoiceChips, FlowGrid, H6, InfoSheet } from '@fs/zion-ui'
import React, { useMemo, useState, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import isEmpty from 'lodash.isempty'
import isEqual from 'lodash.isequal'
import { getSlsTypeByValue } from '@fs/zion-sls'
import { translateRelationshipChips } from '../../../../utils/translationUtils'

export const ALL_CHANGES_FILTER = {
  primary: 'ALL_CHANGES',
}

const PERSON_DETAILS_FILTERS = [
  { primary: 'AGE' },
  { primary: 'EVENT' },
  { primary: 'EVENT', secondary: 'BIRTH', tertiary: 'PLACE', label: 'Birthplace' },
  { primary: 'NAME_GN' },
  { primary: 'MARITAL_STATUS' },
  { primary: 'RACE_OR_COLOR' },
  { primary: 'SEX_CODE' },
  { primary: 'NAME_SURN' },
]

// Function to get all the default, relationship and details' objects to create chips
const getFilterChips = (slsTypes, t) => {
  if (isEmpty(slsTypes)) {
    return { allChanges: [], relationships: [], personDetails: [] }
  }

  // Default object to show all
  const allChanges = [{ label: t('history-viewer.filter.all-changes', 'All Changes'), value: ALL_CHANGES_FILTER }]

  // Getting relationship onjects from the sls types
  const relationships = translateRelationshipChips(slsTypes, t).map((item) => ({
    label: item.label,
    value: { primary: item.value },
  }))

  // Getting person details objects
  const personDetails = PERSON_DETAILS_FILTERS.map((item) => ({
    label: item?.label
      ? item.label
      : (getSlsTypeByValue(item.primary, 'fields', slsTypes) || getSlsTypeByValue(item.primary, 'elements', slsTypes))
          ?.displayLabel,
    value: item,
  }))

  return {
    allChanges,
    relationships,
    personDetails,
  }
}

// Each of the chips sections shown in the filters panel
const ChipSection = memo(({ chips, onChange, selectedValue, title, name }) => (
  <FlowGrid gutters="xxs" orientation="vertical" columnCount={1}>
    {title && <H6>{title}</H6>}
    {chips.length > 0 && (
      <ChoiceChips
        chips={chips.map((chip) => ({
          ...chip,
          selected: isEqual(chip.value, selectedValue),
        }))}
        name={name}
        onSelectionChange={onChange}
      />
    )}
  </FlowGrid>
))

const FiltersPanel = ({ slsTypes, filterSheetOverlay, onChange }) => {
  const { t } = useTranslation()

  // Getting all objects to create the chips
  const { allChanges, personDetails, relationships } = useMemo(() => getFilterChips(slsTypes, t), [slsTypes, t])

  // State to track the selected value
  const [selectedValue, setSelectedValue] = useState(ALL_CHANGES_FILTER)

  // Handler to update the selected value and clear selections from other chips
  const handleSelectionChange = useCallback(
    (chip) => {
      setSelectedValue(chip.value)
      onChange(chip) // Function passed from the parent element to respond to changes on the selection
    },
    [onChange]
  )

  return (
    <InfoSheet size="md" title={t('history-viewer.filter.button', 'Filter')} {...filterSheetOverlay}>
      <ChipSection
        chips={allChanges}
        name="allChanges"
        selectedValue={selectedValue}
        onChange={handleSelectionChange}
      />

      <ChipSection
        title={t('history-viewer.filter.person-details', 'Person Details')}
        chips={personDetails}
        name="details"
        selectedValue={selectedValue}
        onChange={handleSelectionChange}
      />

      <ChipSection
        title={t('history-viewer.filter.relationships', 'Relationships')}
        name="relationsships"
        chips={relationships}
        selectedValue={selectedValue}
        onChange={handleSelectionChange}
      />
    </InfoSheet>
  )
}

export default FiltersPanel
